import * as React from "react";
import { useState, useEffect, ReactNode } from "react";

export type SelectorProps<T extends ReactNode> = {
  label?: string;
  prefix?: string;
  suffix?: string;
  defaultIndex?: number;
  /** @deprecated Use defaultIndex instead */
  default?: number;
  onChoose?: (choice: number) => void;
  choices: T[];
  children?: JSX.Element[];
};

export function Selector<T extends ReactNode>({
  label,
  prefix,
  suffix,
  defaultIndex,
  default: defaultProp,
  onChoose,
  choices,
  children,
}: SelectorProps<T>) {
  const initialChoice = defaultIndex ?? defaultProp ?? 0;
  const [choice, setChoice] = useState(initialChoice);

  useEffect(() => {
    setChoice(defaultIndex ?? defaultProp ?? 0);
  }, [defaultIndex, defaultProp]);

  const select = (newChoice: number) => {
    setChoice(newChoice);
    onChoose?.(newChoice);
  };

  if (choices.length <= 1) {
    return children?.[choice] ?? null;
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        {label && (
          <span className="min-w-[100px] text-sm font-medium text-slate-600 dark:text-slate-300">
            {label}
          </span>
        )}
        {prefix && (
          <span className="text-sm text-slate-500 dark:text-slate-400">
            {prefix}
          </span>
        )}
        <div className="flex flex-wrap gap-1.5">
          {choices.map((choiceLabel, i) => {
            const isSelected = i === choice;
            return (
              <button
                type="button"
                key={i}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-all ${
                  isSelected
                    ? "bg-blue-600 text-white shadow-sm"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
                }`}
                onClick={() => select(i)}
              >
                {choiceLabel}
              </button>
            );
          })}
        </div>
        {suffix && (
          <span className="text-sm text-slate-500 dark:text-slate-400">
            {suffix}
          </span>
        )}
      </div>
      {children?.[choice]}
    </div>
  );
}

export default Selector;
